// src/pages/Solutions/SolutionsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Container, Typography, Paper, Box, Grid, Card, CardContent, CardActions,
  Button, Chip, Divider, Avatar, Rating, Tabs, Tab, Skeleton,
  Alert, CircularProgress, Collapse, List, ListItem,
  ListItemText, ListItemAvatar, Dialog, DialogTitle, DialogContent,
  DialogActions, Pagination, TextField, InputAdornment, FormControl,
  InputLabel, Select, MenuItem, Drawer, Stack,
} from '@mui/material';
import {
  ExpandMore, Assignment, ChatBubbleOutline, LocalOffer, Article,
  Person, CalendarToday, Business, Search, FilterList, Add, Star,
  ModeComment, Visibility, RateReview,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import api from '../../services/api';

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const RENGIGS = {
  green: '#2E7D32',
  lightGreen: '#4CAF50',

  lime: '#CDDC39',
  dark: '#1A2A3A',
  light: '#F8FAFC',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
};

const SolutionsPage = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  const [mainTab, setMainTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Section 1: My Solutions
  const [mySolutions, setMySolutions] = useState([]);
  const [expandedSolution, setExpandedSolution] = useState(null);
  const [taggedProblems, setTaggedProblems] = useState({});
  const [solutionUpdates, setSolutionUpdates] = useState({});

  // Section 2: Business Leads
  const [businessLeads, setBusinessLeads] = useState([]);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Section 3: Potentials
  const [solutionViewers, setSolutionViewers] = useState({});
  const [solutionReviews, setSolutionReviews] = useState({});

  // Section 4: Practical Experience
  const [articles, setArticles] = useState([]);
  const [articlesPage, setArticlesPage] = useState(1);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);

  // Browse All Problems
  const [allProblems, setAllProblems] = useState([]);
  const [loadingBrowse, setLoadingBrowse] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [problemCategory, setProblemCategory] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const isSupplier = user?.user_type === 'supplier' || user?.user_type === 'vendor';

  // ─────────────────────────────────────────────────────────────────────────
  // SECTION 1 — My Solutions
  // The Solution model links to Organization via `provider` FK.
  // Django REST serializes this as `provider_id` (the org's UUID pk).
  // We use the dedicated `/solutions/` endpoint filtered by provider org IDs.
  // ─────────────────────────────────────────────────────────────────────────
  const fetchMySolutions = useCallback(async () => {
    if (!isSupplier) return [];
    try {
      // Step 1: Get all organizations the current user belongs to
      const orgsResponse = await api.get('/organizations/my-organizations/');
      // Handle both paginated { results: [] } and plain array responses
      const orgs = Array.isArray(orgsResponse)
        ? orgsResponse
        : (orgsResponse.results || []);

      if (orgs.length === 0) {
        console.warn('[SolutionsPage] No organizations found for user');
        return [];
      }

      // Collect all org IDs (UUIDs as strings)
      const userOrgIds = orgs.map((org) => String(org.id));
      console.log('[SolutionsPage] User org IDs:', userOrgIds);

      // Step 2: Fetch solutions — try provider filter directly first
      // The backend Solution model has `provider` FK, so the API likely supports
      // ?provider=<uuid> filtering. Try each org, collect all results.
      let mySols = [];

      for (const orgId of userOrgIds) {
        try {
          const res = await api.solutions.list({ provider: orgId, page_size: 100 });
          const sols = res.results || [];
          mySols = [...mySols, ...sols];
        } catch (err) {
          console.warn(`[SolutionsPage] Provider filter failed for org ${orgId}, will fall back`);
        }
      }

      // Deduplicate by id in case of overlapping results
      const seen = new Set();
      mySols = mySols.filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });

      // Step 3: If provider filter returned nothing (filter not supported by backend),
      // fall back to fetching all solutions and filtering client-side.
      if (mySols.length === 0) {
        console.log('[SolutionsPage] No results from provider filter, falling back to client-side filter');
        const allRes = await api.solutions.list({ page_size: 200 });
        const allSols = allRes.results || [];

        // `provider_id` is the FK field Django REST exposes for the provider ForeignKey
        mySols = allSols.filter((sol) => {
          const solProviderId = String(sol.provider_id ?? sol.provider ?? '');
          return userOrgIds.some((id) => id === solProviderId);
        });

        console.log('[SolutionsPage] Client-side filtered solutions:', mySols.length);
      }

      return mySols;
    } catch (err) {
      console.error('[SolutionsPage] Failed to fetch my solutions:', err);
      return [];
    }
  }, [isSupplier]);

  // ─────────────────────────────────────────────────────────────────────────
  // SECTION 1 — Tagged Problems per solution
  // Calls GET /solutions/{id}/tagged-problems/ (action added to SolutionViewSet)
  // Returns: [{ problem_id, title, description, organization_name, status, tagged_at, note }]
  // ─────────────────────────────────────────────────────────────────────────
  const fetchTaggedProblemsForSolution = async (solId) => {
    try {
      const data = await api.get(`/solutions/${solId}/tagged-problems/`);
      // Response is a plain array (not paginated)
      return Array.isArray(data) ? data : (data.results || []);
    } catch (err) {
      console.warn(`[SolutionsPage] Could not fetch tagged problems for solution ${solId}:`, err);
      return [];
    }
  };

  const fetchSolutionUpdates = async (solId) => {
    try {
      const data = await api.get(`/solutions/${solId}/updates/`);
      return data.results || [];
    } catch {
      return [];
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SECTION 3 — Potentials: viewers & reviews
  // ─────────────────────────────────────────────────────────────────────────
  const fetchSolutionViewers = async (solId) => {
    try {
      const data = await api.solutions.getViewerHistory(solId);
      // API may return { viewers: [] } or { results: [] } or a plain array
      return data.viewers || data.results || (Array.isArray(data) ? data : []);
    } catch {
      return [];
    }
  };

  const fetchSolutionReviews = async (solId) => {
    try {
      const data = await api.solutions.getReviews(solId);
      return data.results || (Array.isArray(data) ? data : []);
    } catch {
      return [];
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SECTION 2 — Business Leads: problems the supplier could address
  // ─────────────────────────────────────────────────────────────────────────
  const fetchBusinessLeads = useCallback(async (pageNum = 1) => {
    try {
      setLoadingLeads(true);
      const data = await api.problems.list({
        page: pageNum,
        page_size: 6,
        ordering: '-created_at',
      });
      return { results: data.results || [], count: data.count || 0 };
    } catch (err) {
      console.error('[SolutionsPage] Failed to fetch business leads:', err);
      return { results: [], count: 0 };
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // SECTION 4 — Practical Experience articles
  // Replace mock with a real endpoint when available.
  // ─────────────────────────────────────────────────────────────────────────
  const fetchArticles = useCallback(async (pageNum = 1) => {
    try {
      const data = await api.get('/articles/', { params: { page: pageNum, page_size: 6 } });
      return {
        results: data.results || [],
        count: data.count || 0,
      };
    } catch {
      // Fallback mock while the endpoint is not ready
      const mock = [
        {
          id: 1,
          title: 'How to reduce supply chain costs',
          excerpt: 'Practical tips for leaner procurement...',
          created_at: new Date().toISOString(),
          author: 'John Doe',
          image: null,
          content: 'Full article content goes here.',
        },
        {
          id: 2,
          title: 'AI in procurement',
          excerpt: 'Leveraging AI to find better vendors...',
          created_at: new Date().toISOString(),
          author: 'Jane Smith',
          image: null,
          content: 'Full article content goes here.',
        },
        {
          id: 3,
          title: 'Sustainable sourcing',
          excerpt: 'Integrate ESG criteria into vendor selection...',
          created_at: new Date().toISOString(),
          author: 'Alice Johnson',
          image: null,
          content: 'Full article content goes here.',
        },
      ];
      return { results: mock, count: mock.length };
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Load all supplier dashboard data on mount
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadDashboard = async () => {
      if (!isAuthenticated || !isSupplier) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Fetch my solutions first
        const mySols = await fetchMySolutions();
        if (isMounted.current) setMySolutions(mySols);

        // Fetch per-solution data in parallel
        const [taggedMap, updatesMap, viewersMap, reviewsMap] = [{}, {}, {}, {}];
        await Promise.all(
          mySols.map(async (sol) => {
            const [tagged, updates, viewers, reviews] = await Promise.all([
              fetchTaggedProblemsForSolution(sol.id),
              fetchSolutionUpdates(sol.id),
              fetchSolutionViewers(sol.id),
              fetchSolutionReviews(sol.id),
            ]);
            taggedMap[sol.id]  = tagged;
            updatesMap[sol.id] = updates;
            viewersMap[sol.id] = viewers;
            reviewsMap[sol.id] = reviews;
          })
        );

        if (isMounted.current) {
          setTaggedProblems(taggedMap);
          setSolutionUpdates(updatesMap);
          setSolutionViewers(viewersMap);
          setSolutionReviews(reviewsMap);
        }

        // Business leads & articles in parallel
        const [leads, arts] = await Promise.all([
          fetchBusinessLeads(1),
          fetchArticles(1),
        ]);

        if (isMounted.current) {
          setBusinessLeads(leads.results);
          setLeadsTotal(leads.count);
          setArticles(arts.results);
          setArticlesTotal(arts.count);
        }
      } catch (err) {
        console.error('[SolutionsPage] Dashboard load error:', err);
        if (isMounted.current) setError('Failed to load dashboard. Please refresh the page.');
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    loadDashboard();
  }, [isAuthenticated, isSupplier, fetchMySolutions, fetchBusinessLeads, fetchArticles]);

  // ─────────────────────────────────────────────────────────────────────────
  // Browse All Problems
  // ─────────────────────────────────────────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    try {
      const data = await api.get('/core/categories/');
      if (isMounted.current) setCategories(data?.results || data || []);
    } catch {}
  }, []);

  const fetchAllProblems = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingBrowse(true);
      const params = { page, page_size: 20, ordering: '-created_at' };
      if (searchTerm)      params.search   = searchTerm;
      if (problemCategory) params.category = problemCategory;

      const data = await api.problems.list(params);
      if (isMounted.current) {
        setAllProblems(data.results || []);
        setTotalCount(data.count || 0);
        setTotalPages(Math.max(1, Math.ceil((data.count || 0) / 20)));
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else if (isMounted.current) setError('Failed to load problems.');
    } finally {
      if (isMounted.current) setLoadingBrowse(false);
    }
  }, [isAuthenticated, page, searchTerm, problemCategory, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllProblems();
      fetchCategories();
    }
  }, [isAuthenticated, fetchAllProblems, fetchCategories]);

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────
  const handleLeadsPageChange = async (event, value) => {
    setLeadsPage(value);
    const res = await fetchBusinessLeads(value);
    setBusinessLeads(res.results);
    setLeadsTotal(res.count);
  };

  const handleArticlesPageChange = async (event, value) => {
    setArticlesPage(value);
    const res = await fetchArticles(value);
    setArticles(res.results);
    setArticlesTotal(res.count);
  };

  const handleExpandSolution = (solId) => {
    setExpandedSolution(expandedSolution === solId ? null : solId);
  };

  const openArticleDialog = (article) => {
    setSelectedArticle(article);
    setArticleDialogOpen(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setProblemCategory('');
    setPage(1);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Filter drawer for Browse All Problems
  // ─────────────────────────────────────────────────────────────────────────
  const FilterContent = () => (
    <Box sx={{ p: 3, width: isMobile ? 280 : 320 }}>
      <Typography variant="h6" mb={2} sx={{ fontWeight: 800, color: RENGIGS.dark }}>
        Filters
      </Typography>

      {categories.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel sx={{ color: RENGIGS.green }}>Category</InputLabel>
          <Select
            value={problemCategory}
            onChange={(e) => { setProblemCategory(e.target.value); setPage(1); }}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Button
        fullWidth
        variant="outlined"
        onClick={() => { clearFilters(); setFilterDrawerOpen(false); }}
        sx={{ borderColor: RENGIGS.green, color: RENGIGS.green }}
      >
        Reset Filters
      </Button>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Guards
  // ─────────────────────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Non-supplier: simplified browse view
  // ─────────────────────────────────────────────────────────────────────────
  if (!isSupplier) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: RENGIGS.dark, mb: 2 }}>
          Browse All Problems
        </Typography>
        <Alert severity="info" sx={{ mb: 4 }}>
          You are viewing as an organization. Browse open challenges below or post your own.
        </Alert>
        <Paper elevation={0} sx={{ p: 1.5, mb: 3, border: `1px solid ${RENGIGS.border}`, borderRadius: 2 }}>
          <TextField
            fullWidth
            placeholder="Search problems..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search sx={{ color: RENGIGS.green }} /></InputAdornment>
              ),
            }}
          />
        </Paper>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {loadingBrowse ? 'Loading…' : `${totalCount} problem${totalCount !== 1 ? 's' : ''}`}
          </Typography>
          <Button startIcon={<FilterList />} onClick={() => setFilterDrawerOpen(true)} variant="outlined" size="small">
            Filters
          </Button>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {loadingBrowse && (
          <Stack spacing={2}>{[1,2,3,4,5].map((i) => <Skeleton key={i} height={110} />)}</Stack>
        )}
        {!loadingBrowse && allProblems.length === 0 && (
          <Alert severity="info">No problems found.</Alert>
        )}
        {!loadingBrowse && allProblems.map((prob) => (
          <Card key={prob.id} sx={{ mb: 2, cursor: 'pointer', '&:hover': { boxShadow: 3 }, transition: '0.2s' }}
            onClick={() => navigate(`/problems/${prob.id}`)}>
            <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <Avatar sx={{ bgcolor: RENGIGS.green, width: 40, height: 40, flexShrink: 0 }}>
                <Business fontSize="small" />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" noWrap>{prob.title}</Typography>
                <Typography variant="body2" color="text.secondary">{prob.organization_name}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }} noWrap>
                  {prob.description?.substring(0, 120)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {prob.category_name && <Chip label={prob.category_name} size="small" variant="outlined" />}
                  <Typography variant="caption" color="text.secondary">{formatDate(prob.created_at)}</Typography>
                </Box>
              </Box>
              <Chip
                icon={<LocalOffer fontSize="small" />}
                label={`${prob.tagged_solutions_count || 0} solutions`}
                size="small"
                sx={{ flexShrink: 0 }}
              />
            </CardContent>
          </Card>
        ))}
        {totalPages > 1 && (
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} sx={{ mt: 3 }} />
        )}
        <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
          <FilterContent />
        </Drawer>
      </Container>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Supplier dashboard
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: RENGIGS.dark, mb: 1 }}>
          Solutions Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your offerings, find business leads, and track engagement
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: `1px solid ${RENGIGS.border}`, borderRadius: 2, mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={(e, v) => setMainTab(v)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons="auto"
          sx={{
            bgcolor: RENGIGS.light,
            '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '0.9rem' },
            '& .Mui-selected': { color: `${RENGIGS.green} !important` },
            '& .MuiTabs-indicator': { bgcolor: RENGIGS.green },
          }}
        >
          <Tab label="My Solutions" />
          <Tab label="Business Leads" />
          <Tab label="Potentials" />
          <Tab label="Practical Experience" />
        </Tabs>

        <Box sx={{ p: 3 }}>

          {/* ── Tab 0: My Solutions ──────────────────────────────────────── */}
          {mainTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: RENGIGS.dark }}>
                  My Solutions
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/solutions/create')}
                  sx={{ bgcolor: RENGIGS.green, textTransform: 'none' }}
                >
                  List New Solution
                </Button>
              </Box>

              {loading ? (
                <Stack spacing={2}>{[1,2,3].map((i) => <Skeleton key={i} height={120} />)}</Stack>
              ) : mySolutions.length === 0 ? (
                <Alert severity="info">
                  You haven't listed any solutions yet. Click "List New Solution" to get started.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {mySolutions.map((sol) => (
                    <Grid item xs={12} key={sol.id}>
                      <Card sx={{ borderRadius: 2, border: `1px solid ${RENGIGS.border}` }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: RENGIGS.dark }}>
                                {sol.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Listed {formatDate(sol.created_at)}
                              </Typography>
                            </Box>
                            <Chip
                              label={sol.status || 'Published'}
                              size="small"
                              sx={{ bgcolor: RENGIGS.lightGreen, color: '#fff' }}
                            />
                          </Box>

                          <Typography variant="body2" sx={{ mt: 2, color: '#475569' }}>
                            {sol.tagline || sol.description?.substring(0, 200)}
                          </Typography>

                          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/solutions/${sol.id}`)}
                              sx={{ borderColor: RENGIGS.green, color: RENGIGS.green, textTransform: 'none' }}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); navigate(`/solutions/${sol.id}/edit`); }}
                              sx={{ textTransform: 'none', borderColor: RENGIGS?.border }}
                            >
                              Edit
                            </Button>
                          </Box>

                          <Button
                            size="small"
                            onClick={() => handleExpandSolution(sol.id)}
                            sx={{ mt: 1, color: RENGIGS.green, textTransform: 'none' }}
                            endIcon={
                              <ExpandMore
                                sx={{
                                  transform: expandedSolution === sol.id ? 'rotate(180deg)' : 'none',
                                  transition: '0.2s',
                                }}
                              />
                            }
                          >
                            {expandedSolution === sol.id ? 'Hide Details' : 'View Tagged Problems & Updates'}
                          </Button>
                        </CardContent>

                        <Collapse in={expandedSolution === sol.id}>
                          <Divider />
                          <CardContent sx={{ bgcolor: RENGIGS.light }}>

                            {/* ── Tagged Problems ── */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <Assignment sx={{ color: RENGIGS.green, fontSize: 20 }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: RENGIGS.dark }}>
                                Problems Tagged to this Solution
                              </Typography>
                              <Chip
                                label={taggedProblems[sol.id]?.length || 0}
                                size="small"
                                sx={{ bgcolor: RENGIGS.green, color: '#fff', fontWeight: 700, ml: 'auto' }}
                              />
                            </Box>

                            {!taggedProblems[sol.id] || taggedProblems[sol.id].length === 0 ? (
                              <Box sx={{ py: 2, px: 2, bgcolor: '#fff', borderRadius: 1, border: `1px dashed ${RENGIGS.border}`, textAlign: 'center' }}>
                                <LocalOffer sx={{ fontSize: 32, color: RENGIGS.border, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  No problems have tagged this solution yet.
                                </Typography>
                              </Box>
                            ) : (
                              <Stack spacing={1.5}>
                                {taggedProblems[sol.id].map((prob) => (
                                  <Box
                                    key={prob.tag_id || prob.problem_id}
                                    sx={{
                                      p: 2,
                                      bgcolor: '#fff',
                                      borderRadius: 1.5,
                                      border: `1px solid ${RENGIGS.border}`,
                                      cursor: 'pointer',
                                      transition: '0.15s',
                                      '&:hover': { borderColor: RENGIGS.green, boxShadow: 1 },
                                    }}
                                    onClick={() => navigate(`/problems/${prob.problem_id}`)}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {/* Org + category row */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                          <Avatar sx={{ bgcolor: RENGIGS.lightGreen, width: 22, height: 22 }}>
                                            <Business sx={{ fontSize: 13 }} />
                                          </Avatar>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: RENGIGS.dark }}>
                                            {prob.organization_name}
                                          </Typography>
                                          {prob.category_name && (
                                            <Chip label={prob.category_name} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                          )}
                                        </Box>

                                        {/* Problem title */}
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: RENGIGS.dark, mb: 0.5 }}>
                                          {prob.title}
                                        </Typography>

                                        {/* Description snippet */}
                                        {prob.description && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            {prob.description}…
                                          </Typography>
                                        )}

                                        {/* Note from tagger */}
                                        {prob.note && (
                                          <Box sx={{ mt: 0.5, px: 1, py: 0.5, bgcolor: RENGIGS.light, borderRadius: 1, borderLeft: `3px solid ${RENGIGS.green}` }}>
                                            <Typography variant="caption" color="text.secondary">
                                              Note: {prob.note}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>

                                      {/* Right: status + date */}
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                                        <Chip
                                          label={prob.status || 'open'}
                                          size="small"
                                          sx={{
                                            bgcolor: prob.status === 'published' || prob.status === 'open'
                                              ? '#E8F5E9' : '#FFF3E0',
                                            color: prob.status === 'published' || prob.status === 'open'
                                              ? RENGIGS.green : '#E65100',
                                            fontWeight: 600,
                                            fontSize: '0.65rem',
                                          }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                          Tagged {formatDate(prob.tagged_at)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                ))}
                              </Stack>
                            )}

                            <Divider sx={{ my: 2.5 }} />

                            {/* ── Latest Comments & Updates ── */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <ChatBubbleOutline sx={{ color: RENGIGS.green, fontSize: 20 }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: RENGIGS.dark }}>
                                Latest Comments & Updates
                              </Typography>
                            </Box>

                            {!solutionUpdates[sol.id] || solutionUpdates[sol.id].length === 0 ? (
                              <Box sx={{ py: 2, px: 2, bgcolor: '#fff', borderRadius: 1, border: `1px dashed ${RENGIGS.border}`, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                  No updates yet.
                                </Typography>
                              </Box>
                            ) : (
                              <List dense disablePadding>
                                {solutionUpdates[sol.id].slice(0, 3).map((upd) => (
                                  <ListItem
                                    key={upd.id}
                                    sx={{ px: 2, py: 1, bgcolor: '#fff', borderRadius: 1, border: `1px solid ${RENGIGS.border}`, mb: 1 }}
                                  >
                                    <ListItemAvatar>
                                      <Avatar sx={{ bgcolor: RENGIGS.light, width: 28, height: 28 }}>
                                        <Person sx={{ fontSize: 16, color: RENGIGS.green }} />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={upd.content}
                                      secondary={`by ${upd.user_name || 'User'} · ${formatDate(upd.created_at)}`}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                      secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </CardContent>
                        </Collapse>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* ── Tab 1: Business Leads ────────────────────────────────────── */}
          {mainTab === 1 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: RENGIGS.dark, mb: 3 }}>
                Problems Matching Your Expertise
              </Typography>

              {loadingLeads ? (
                <Stack spacing={2}>{[1,2,3,4].map((i) => <Skeleton key={i} height={160} />)}</Stack>
              ) : businessLeads.length === 0 ? (
                <Alert severity="info">No business leads found at this time.</Alert>
              ) : (
                <Grid container spacing={3}>
                  {businessLeads.map((prob) => (
                    <Grid item xs={12} md={6} key={prob.id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 2,
                          border: `1px solid ${RENGIGS.border}`,
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Avatar sx={{ bgcolor: RENGIGS.green, width: 32, height: 32 }}>
                              <Business fontSize="small" />
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {prob.organization_name}
                            </Typography>
                            <Chip
                              size="small"
                              label={prob.category_name || 'General'}
                              variant="outlined"
                            />
                          </Box>

                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, mb: 1, cursor: 'pointer', '&:hover': { color: RENGIGS.green } }}
                            onClick={() => navigate(`/problems/${prob.id}`)}
                          >
                            {prob.title}
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {prob.description?.substring(0, 150)}…
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Chip
                              icon={<LocalOffer fontSize="small" />}
                              label={`${prob.tagged_solutions_count || 0} solutions`}
                              size="small"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(prob.created_at)}
                            </Typography>
                          </Box>
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            onClick={() => navigate(`/problems/${prob.id}`)}
                            sx={{ color: RENGIGS.green, textTransform: 'none' }}
                          >
                            View Challenge
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {leadsTotal > 6 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={Math.ceil(leadsTotal / 6)}
                    page={leadsPage}
                    onChange={handleLeadsPageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 2: Potentials ────────────────────────────────────────── */}
          {mainTab === 2 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: RENGIGS.dark, mb: 3 }}>
                Potentials
              </Typography>

              {loading ? (
                <Stack spacing={2}>{[1,2,3].map((i) => <Skeleton key={i} height={120} />)}</Stack>
              ) : mySolutions.length === 0 ? (
                <Alert severity="info">List a solution first to start tracking engagement.</Alert>
              ) : (
                <Grid container spacing={3}>
                  {/* Viewers */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: RENGIGS.dark, mb: 2 }}>
                      <Visibility sx={{ mr: 1, verticalAlign: 'middle', color: RENGIGS.green }} />
                      Who's Viewing Your Solutions
                    </Typography>
                    {mySolutions.map((sol) => (
                      <Card key={sol.id} sx={{ mb: 2, border: `1px solid ${RENGIGS.border}`, borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {sol.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Total views: {sol.views_count || solutionViewers[sol.id]?.length || 0}
                          </Typography>
                          <Box>
                            {(solutionViewers[sol.id] || []).slice(0, 5).map((org, idx) => (
                              <Chip
                                key={idx}
                                label={org.name || org.organization_name || 'Organization'}
                                size="small"
                                sx={{ mr: 1, mb: 1 }}
                              />
                            ))}
                            {(!solutionViewers[sol.id] || solutionViewers[sol.id].length === 0) && (
                              <Typography variant="body2" color="text.secondary">
                                No views recorded yet.
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Grid>

                  {/* Reviews */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: RENGIGS.dark, mb: 2 }}>
                      <RateReview sx={{ mr: 1, verticalAlign: 'middle', color: RENGIGS.green }} />
                      Reviews & Ratings
                    </Typography>
                    {mySolutions.map((sol) => (
                      <Card key={sol.id} sx={{ mb: 2, border: `1px solid ${RENGIGS.border}`, borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            {sol.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Rating value={sol.rating_avg || 0} readOnly size="small" precision={0.1} />
                            <Typography variant="body2" color="text.secondary">
                              {sol.rating_avg ? sol.rating_avg.toFixed(1) : '0.0'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({solutionReviews[sol.id]?.length || sol.review_count || 0} reviews)
                            </Typography>
                          </Box>
                          {(solutionReviews[sol.id] || []).slice(0, 2).map((rev, idx) => (
                            <Box
                              key={idx}
                              sx={{ mt: 1, p: 1.5, bgcolor: RENGIGS.light, borderRadius: 1, border: `1px solid ${RENGIGS.border}` }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                  {rev.reviewer_name || rev.reviewed_by_name || 'Reviewer'}
                                </Typography>
                                <Rating value={rev.overall_rating} readOnly size="small" sx={{ fontSize: '0.8rem' }} />
                              </Box>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {rev.comments?.substring(0, 100)}
                                {rev.comments?.length > 100 ? '…' : ''}
                              </Typography>
                            </Box>
                          ))}
                          {(!solutionReviews[sol.id] || solutionReviews[sol.id].length === 0) && (
                            <Typography variant="body2" color="text.secondary">
                              No reviews yet.
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* ── Tab 3: Practical Experience ──────────────────────────────── */}
          {mainTab === 3 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: RENGIGS.dark, mb: 3 }}>
                Sharing Practical Experience
              </Typography>

              <Grid container spacing={3}>
                {articles.map((art) => (
                  <Grid item xs={12} md={4} key={art.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        border: `1px solid ${RENGIGS.border}`,
                      }}
                    >
                      <Box
                        sx={{
                          height: 140,
                          bgcolor: RENGIGS.light,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {art.image ? (
                          <img
                            src={art.image}
                            alt={art.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Article sx={{ fontSize: 60, color: RENGIGS.green, opacity: 0.4 }} />
                        )}
                      </Box>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          {art.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {art.excerpt?.substring(0, 100)}…
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', flexWrap: 'wrap' }}>
                          <Person fontSize="small" />
                          <Typography variant="caption">{art.author || 'Admin'}</Typography>
                          <CalendarToday fontSize="small" />
                          <Typography variant="caption">{formatDate(art.created_at)}</Typography>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          onClick={() => openArticleDialog(art)}
                          sx={{ color: RENGIGS.green, textTransform: 'none' }}
                        >
                          Read More
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {articlesTotal > 6 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={Math.ceil(articlesTotal / 6)}
                    page={articlesPage}
                    onChange={handleArticlesPageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* ── Browse All Problems ──────────────────────────────────────────── */}
      <Divider sx={{ my: 4 }} />
      <Typography variant="h4" sx={{ fontWeight: 800, color: RENGIGS.dark, mb: 3 }}>
        Browse All Problems
      </Typography>
      <Paper elevation={0} sx={{ p: 1.5, mb: 3, borderRadius: '12px', border: `1px solid ${RENGIGS.border}` }}>
        <TextField
          fullWidth
          placeholder="Search problems..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search sx={{ color: RENGIGS.green }} /></InputAdornment>
            ),
          }}
        />
      </Paper>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {loadingBrowse ? 'Loading…' : `${totalCount} problem${totalCount !== 1 ? 's' : ''} found`}
        </Typography>
        <Button
          startIcon={<FilterList />}
          onClick={() => setFilterDrawerOpen(true)}
          variant="outlined"
          size="small"
        >
          Filters
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {loadingBrowse && (
        <Stack spacing={2}>{[1,2,3,4,5].map((i) => <Skeleton key={i} height={110} />)}</Stack>
      )}
      {!loadingBrowse && allProblems.length === 0 && (
        <Alert severity="info">No problems found. Try adjusting your filters.</Alert>
      )}
      {!loadingBrowse && allProblems.map((prob) => (
        <Card
          key={prob.id}
          sx={{ mb: 2, cursor: 'pointer', '&:hover': { boxShadow: 3 }, transition: '0.2s' }}
          onClick={() => navigate(`/problems/${prob.id}`)}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Avatar sx={{ bgcolor: RENGIGS.green, width: 44, height: 44, flexShrink: 0 }}>
              <Business fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" noWrap>{prob.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {prob.organization_name}
              </Typography>
              <Typography variant="body2" noWrap>
                {prob.description?.substring(0, 140)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {prob.category_name && (
                  <Chip label={prob.category_name} size="small" variant="outlined" />
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatDate(prob.created_at)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
              <Chip
                icon={<LocalOffer fontSize="small" />}
                label={`${prob.tagged_solutions_count || 0} solutions`}
                size="small"
                sx={{ bgcolor: RENGIGS.light }}
              />
              <Chip
                label={prob.status || 'open'}
                size="small"
                sx={{
                  bgcolor: prob.status === 'published' || prob.status === 'open' ? '#E8F5E9' : '#FFF3E0',
                  color:   prob.status === 'published' || prob.status === 'open' ? RENGIGS.green : '#E65100',
                  fontWeight: 600,
                }}
              />
            </Box>
          </CardContent>
        </Card>
      ))}

      {totalPages > 1 && (
        <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} sx={{ mt: 3 }} />
      )}

      <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
        <FilterContent />
      </Drawer>

      {/* Article Dialog */}
      <Dialog open={articleDialogOpen} onClose={() => setArticleDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedArticle && (
          <>
            <DialogTitle sx={{ background: RENGIGS.gradient, color: 'white' }}>
              {selectedArticle.title}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: RENGIGS.green }}><Person /></Avatar>
                <Box>
                  <Typography variant="subtitle2">{selectedArticle.author}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(selectedArticle.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {selectedArticle.content || selectedArticle.excerpt}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setArticleDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default SolutionsPage;